import React, { useState, useEffect } from 'react';
import {
  message,
  Button,
  Row,
  Form,
  Spin,
  Input,
  Typography,
  Tooltip,
  Tabs,
  Descriptions,
  Skeleton,
  Col,
  Switch,
  message as antdMsg
} from 'antd';
import { InfoCircleOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader/PageHeader';
import ContentCustom from '../../../components/ContentCustom/ContentCustom';
import useAuthContext from '../../../contexts/AuthContext';
import useFields from '../MyBrokerTeam/fields';
import useGenerateFormItem from '../../../utils/GenerateFormItem';

const { TabPane } = Tabs;

const ProductsSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = useState(false);
  const { dispatchAPI, user, setProdAccess } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const generateFields = useGenerateFormItem();
  const { getControlled_fit_fields, getBlocked_fit_fields } = useFields();
  const [products, setProducts] = useState([]);
  const [products_access, setProducts_access] = useState();
  const [refresh, setRefresh] = useState(false);

  const advAccess = ['full_fit', 'blocked_fit', 'controlled_fit'];

  const getUser = async (prodAccess) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/users/${user?._id}?populate=permissionManagement.products_access.product`
      });
      const newProducts = data.permissionManagement.products_access;
      if (newProducts) {
        prodAccess.map((p) => {
          const currP = p;
          const prod = newProducts.find(
            (pro) => pro.product._id === p.product._id
          );
          if (prod) {
            currP.advancedManagement = prod.advancedManagement;
            currP.permitted = prod.permitted;
          }
          return currP;
        });
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
    return prodAccess;
  };

  const initProducts = async (newProducts) => {
    let prodAccess = [];
    // eslint-disable-next-line array-callback-return
    newProducts.map((p) => {
      prodAccess.push({
        product: p,
        permitted: false,
        advancedManagement: {
          value: advAccess[0],
          params: {}
        }
      });
    });
    prodAccess = await getUser(prodAccess);
    setProducts_access(prodAccess);
  };

  const getProducts = async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', { url: '/products' });
      await setProducts(data);
      await initProducts(data);
    } catch (e) {
      if (e.response) message.error(e.response.status);
    }
    setIsLoading(false);
  };

  const handleChange = (value, product) => {
    // eslint-disable-next-line no-param-reassign
    product.advancedManagement.value = value;
  };

  const changeProductAccess = (value, product) => {
    // eslint-disable-next-line no-param-reassign
    product.permitted = value;
    setRefresh(!refresh);
  };

  const handleSubmit = async (values) => {
    setSaveLoading(true);
    products_access.map((product) => {
      const prod = product;
      let val;
      if (prod.permitted) {
        switch (prod.advancedManagement.value) {
          case 'blocked_fit':
            val = values[prod.product._id];
            prod.advancedManagement.params = {
              commission_rate: val.blocked_fit_commission_rate,
              brokerage_fees: val.blocked_fit_brokerage_fees,
              administration_fees: val.blocked_fit_administration_fees
            };
            break;
          case 'controlled_fit':
            val = values[prod.product._id];
            prod.advancedManagement.params = {
              commission_rate: {
                min: val.controlled_fit_commission_rate.commission_rate_minVal,
                max: val.controlled_fit_commission_rate.commission_rate_maxVal
              },
              brokerage_fees: {
                min: val.controlled_fit_brokerage_fees.brokerage_fees_minVal,
                max: val.controlled_fit_brokerage_fees.brokerage_fees_maxVal
              },
              administration_fees: {
                min: val.controlled_fit_administration_fees
                  .administration_fees_minVal,
                max: val.controlled_fit_administration_fees
                  .administration_fees_maxVal
              }
            };
            break;
          default:
            break;
        }
        return prod;
      }
      return {};
    });
    const newUser = user;
    delete newUser?.photo;
    const objectToSave = {
      ...newUser,
      permissionManagement: { products_access },
      insurer: newUser?.insurer._id
    };
    try {
      await dispatchAPI('PATCH', {
        url: `/users/${user?._id}`,
        body: objectToSave
      });
      antdMsg.success('Configuration enregistrée');
      navigate({
        pathname: `/`
      });
      await setProdAccess(products_access);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setSaveLoading(false);
  };

  useEffect(() => {
    (async () => {
      await getProducts();
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageHeader title={t('myBrokerParams.title')} />
      <ContentCustom>
        <Skeleton loading={isLoading}>
          <Form form={form} layout="horizontal" onFinish={handleSubmit}>
            {products_access &&
              products &&
              products_access.map((p) => {
                return (
                  <Row style={{ width: '100%' }}>
                    <Row
                      justify="space-between"
                      style={{ marginBottom: 16, width: '100%' }}
                    >
                      <Col>
                        <Typography.Text level={4}>
                          {t(`${p.product.type.toLowerCase()}.title`)}
                        </Typography.Text>
                      </Col>
                      <Col>
                        <Switch
                          checkedChildren="Active"
                          unCheckedChildren="Non Active"
                          defaultChecked={p.permitted}
                          onChange={(value) => changeProductAccess(value, p)}
                        />
                      </Col>
                    </Row>
                    {p.permitted && (
                      <Descriptions>
                        <Descriptions.Item>
                          <Tabs
                            defaultActiveKey={p.advancedManagement.value}
                            onChange={(value) => handleChange(value, p)}
                            style={{ width: '100%' }}
                          >
                            <TabPane
                              className="myparams-tabpane"
                              tab={
                                <span>
                                  {t('settings.team.full_fit')}
                                  <Tooltip
                                    title={t('settings.team.tooltip.tooltip_1')}
                                  >
                                    <InfoCircleOutlined
                                      style={{ marginLeft: 6, fontSize: 16 }}
                                    />
                                  </Tooltip>
                                </span>
                              }
                              key="full_fit"
                            >
                              {t('settings.team.no_config')}
                            </TabPane>
                            <TabPane
                              className="myparams-tabpane"
                              tab={
                                <span>
                                  {t('settings.team.blocked_fit')}
                                  <Tooltip
                                    title={t('settings.team.tooltip.tooltip_2')}
                                  >
                                    <InfoCircleOutlined
                                      style={{ marginLeft: 6, fontSize: 16 }}
                                    />
                                  </Tooltip>
                                </span>
                              }
                              key="blocked_fit"
                            >
                              <Form.Item name={[p.product._id]}>
                                <Input.Group>
                                  {getBlocked_fit_fields(p.product._id).map(
                                    (field) => {
                                      if (
                                        p.advancedManagement.value ===
                                          'blocked_fit' &&
                                        p.advancedManagement.params
                                      ) {
                                        const name = field.name[1].replace(
                                          'blocked_fit_',
                                          ''
                                        );
                                        // eslint-disable-next-line no-param-reassign
                                        field.initialValue =
                                          p.advancedManagement.params[name];
                                      }
                                      return generateFields(
                                        'settings.team',
                                        field
                                      );
                                    }
                                  )}
                                </Input.Group>
                              </Form.Item>
                            </TabPane>
                            <TabPane
                              className="myparams-tabpane"
                              tab={
                                <span>
                                  {t('settings.team.controlled_fit')}
                                  <Tooltip
                                    title={t('settings.team.tooltip.tooltip_3')}
                                  >
                                    <InfoCircleOutlined
                                      style={{ marginLeft: 6, fontSize: 16 }}
                                    />
                                  </Tooltip>
                                </span>
                              }
                              key="controlled_fit"
                            >
                              <Form.Item name={[p.product._id]}>
                                <Input.Group>
                                  {getControlled_fit_fields(p.product._id).map(
                                    (field) => {
                                      if (
                                        p.advancedManagement.value ===
                                          'controlled_fit' &&
                                        p.advancedManagement.params
                                      ) {
                                        const inputnames = [
                                          `${field.inputName}_minVal`,
                                          `${field.inputName}_maxVal`
                                        ];
                                        const data =
                                          p.advancedManagement.params[
                                            field.inputName
                                          ];
                                        if (data) {
                                          form.setFieldsValue({
                                            [p.product._id]: {
                                              [`controlled_fit_${field.inputName}`]:
                                                {
                                                  // eslint-disable-next-line react/prop-types
                                                  [inputnames[0]]: data.min,
                                                  // eslint-disable-next-line react/prop-types
                                                  [inputnames[1]]: data.max
                                                }
                                            }
                                          });
                                        }
                                      }
                                      return generateFields(
                                        'settings.team',
                                        field
                                      );
                                    }
                                  )}
                                </Input.Group>
                              </Form.Item>
                            </TabPane>
                          </Tabs>
                        </Descriptions.Item>
                      </Descriptions>
                    )}
                  </Row>
                );
              })}
            <Row justify="end" style={{ marginTop: 28 }}>
              <Button type="add" htmlType="submit">
                <Spin spinning={saveLoading} style={{ marginRight: 10 }} />
                {t('buttons.save')}
                <CheckOutlined />
              </Button>
            </Row>
          </Form>
        </Skeleton>
      </ContentCustom>
    </>
  );
};

export default ProductsSettings;
